import { c as _c } from "react/compiler-runtime";
import { BinIcon, PlusIcon } from '@systemeio/ui/icons';
import { DatePicker, Hint, NeutralButton, NeutralTypeEnum, TimePicker } from '@systemeio/ui';
import { greenwichTimeZone } from 'modules/calendar/constants';
import { getDateOnly, shortDateToGreenwichLongDate } from 'modules/calendar/utils/date-time-formatters';
import React from 'react';
import ButtonLikeLink from 'shared/components/button-like-link';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { twMerge } from 'tailwind-merge';
import { AvailabilitySlot, AvailabilitySlotId } from '../types/availability-interface';
import { AvailabilitySlotsSkeleton } from './availability-slots-skeleton';
interface DateSpecificAvailabilitySlotsProps {
  slots: AvailabilitySlot[];
  addSlot: (slot: Omit<AvailabilitySlot, 'id'>) => void;
  updateSlot: (slot: AvailabilitySlot) => void;
  removeSlots: (slotIds: AvailabilitySlotId[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  titleClassName?: string;
}
export function DateSpecificAvailabilitySlots(t0) {
  const $ = _c(46);
  const {
    slots,
    addSlot,
    updateSlot,
    removeSlots,
    isLoading,
    disabled,
    minDate,
    maxDate,
    titleClassName
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale
  } = useUser();
  const t1 = slots.length > 0 && "mb-5";
  let t2;
  if ($[0] !== t1 || $[1] !== titleClassName) {
    t2 = twMerge("flex w-full flex-col items-start gap-2 md:flex-row md:items-center md:justify-between", t1, titleClassName);
    $[0] = t1;
    $[1] = titleClassName;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("dashboard.calendar.date_specific_availability_tooltip");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("dashboard.calendar.date_specific_availability");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <p>{t4}</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <Hint label={t3}>{t5}</Hint>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== addSlot) {
    t7 = () => {
      addSlot({
        date: "",
        startTime: null,
        endTime: null,
        dayOfWeek: null
      });
    };
    $[12] = addSlot;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <PlusIcon />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t) {
    t9 = t("dashboard.calendar.add_date_slot");
    $[15] = t;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== disabled || $[18] !== t7 || $[19] !== t9) {
    t10 = <ButtonLikeLink type="button" onClick={t7} className="flex items-center gap-1 text-sm" disabled={disabled}>{t8}{t9}</ButtonLikeLink>;
    $[17] = disabled;
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t2 || $[23] !== t6) {
    t11 = <div className={t2}>{t6}{t10}</div>;
    $[21] = t10;
    $[22] = t2;
    $[23] = t6;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== isLoading) {
    t12 = isLoading && <AvailabilitySlotsSkeleton />;
    $[25] = isLoading;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== disabled || $[28] !== intlLocale || $[29] !== isLoading || $[30] !== maxDate || $[31] !== minDate || $[32] !== removeSlots || $[33] !== slots || $[34] !== t || $[35] !== updateSlot || $[36] !== user?.createdAt || $[37] !== user?.dateFormat || $[38] !== user?.timeFormat) {
    t13 = !isLoading && slots?.map(slot => <div key={slot.id} className="border-text-secondary/30 flex flex-col gap-2 border-t py-5 last:border-b md:justify-between 2xl:flex-row 2xl:gap-10"><div><DatePicker value={slot.date && shortDateToGreenwichLongDate(slot.date)} onChange={dateTime => {
          if (dateTime) {
            updateSlot({
              ...slot,
              date: getDateOnly(dateTime)
            });
          }
        }} minDate={minDate} maxDate={maxDate} placeholder={t("global.select_date")} disabled={disabled} timezone={greenwichTimeZone} createdAt={user?.createdAt} intlLocale={intlLocale} /></div><div className="flex flex-1 md:justify-between"><div className="[&>input]:w-full flex flex-1 gap-1 md:gap-5"><TimePicker seconds={slot.startTime} onChangeSeconds={seconds => {
            updateSlot({
              ...slot,
              startTime: seconds
            });
          }} disabled={disabled} userDateFormat={user?.dateFormat} userTimeFormat={user?.timeFormat} /><TimePicker seconds={slot.endTime} onChangeSeconds={seconds_0 => {
            updateSlot({
              ...slot,
              endTime: seconds_0
            });
          }} disabled={disabled} userDateFormat={user?.dateFormat} userTimeFormat={user?.timeFormat} /></div><NeutralButton type="button" neutralType={NeutralTypeEnum.danger} onClick={() => removeSlots([slot.id])} disabled={disabled}><BinIcon width={16} height={16} className="fill-text-primary group-hover:fill-danger transition-all" /><span className="hidden md:block">{t("global.remove")}</span></NeutralButton></div></div>);
    $[27] = disabled;
    $[28] = intlLocale;
    $[29] = isLoading;
    $[30] = maxDate;
    $[31] = minDate;
    $[32] = removeSlots;
    $[33] = slots;
    $[34] = t;
    $[35] = updateSlot;
    $[36] = user?.createdAt;
    $[37] = user?.dateFormat;
    $[38] = user?.timeFormat;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t12 || $[41] !== t13) {
    t14 = <div>{t12}{t13}</div>;
    $[40] = t12;
    $[41] = t13;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== t11 || $[44] !== t14) {
    t15 = <>{t11}{t14}</>;
    $[43] = t11;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
}